import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "../utils/supabaseClient";

export default function CouponLanding() {
  const { couponId } = useParams();
  const [coupon, setCoupon] = useState(null);
  const [expired, setExpired] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [postcode, setPostcode] = useState("");

  useEffect(() => {
    async function fetchCoupon() {
      const { data, error } = await supabase
        .from("coupons")
        .select(
          `
          *,
          businesses (
            name,
            logo_url,
            brand_color,
            tagline
          )
        `
        )
        .eq("id", couponId)
        .single();

      if (data) {
        setCoupon(data);
        if (new Date(data.expiry) < new Date()) setExpired(true);

        await supabase
          .from("coupons")
          .update({ views: (data.views || 0) + 1 })
          .eq("id", couponId);
      } else {
        console.error("Coupon fetch error:", error);
      }
    }

    fetchCoupon();
  }, [couponId]);

  const handleClaim = async () => {
    if (!postcode || expired || claimed) return;

    await supabase.from("coupon_claims").insert({
      coupon_id: coupon.id,
      postcode,
      claimed_at: new Date().toISOString(),
    });

    await supabase
      .from("coupons")
      .update({ claims: (coupon.claims || 0) + 1 })
      .eq("id", couponId);

    setClaimed(true);
  };

  if (!coupon) return <p>Loading offer...</p>;

  const canonicalUrl = `https://postcode.coupons/coupon/${couponId}`;

  return (
    <>
      <Helmet>
        {/* BASIC SEO */}
        <title>{coupon.title} | Local Coupon</title>
        <meta
          name="description"
          content={`Claim this local offer from ${coupon.businesses.name}. ${coupon.discount}% off — expires ${coupon.expiry}.`}
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* OPEN GRAPH */}
        <meta property="og:title" content={coupon.title} />
        <meta
          property="og:description"
          content={`Local offer from ${coupon.businesses.name}. ${coupon.discount}% off — expires ${coupon.expiry}.`}
        />
        <meta property="og:image" content={coupon.businesses.logo_url} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="product" />

        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={coupon.title} />
        <meta
          name="twitter:description"
          content={`Local offer from ${coupon.businesses.name}. ${coupon.discount}% off — expires ${coupon.expiry}.`}
        />
        <meta name="twitter:image" content={coupon.businesses.logo_url} />

        {/* STRUCTURED DATA — Offer + Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Offer",
            name: coupon.title,
            price: "0",
            priceCurrency: "GBP",
            availability: expired ? "https://schema.org/Expired" : "https://schema.org/InStock",
            validThrough: coupon.expiry,
            url: canonicalUrl,
            seller: {
              "@type": "LocalBusiness",
              name: coupon.businesses.name,
              image: coupon.businesses.logo_url,
              description: coupon.businesses.tagline,
            },
          })}
        </script>
      </Helmet>

      <div style={{ padding: "20px", textAlign: "center" }}>
        <img
          src={coupon.businesses.logo_url}
          alt="Logo"
          style={{ maxWidth: "160px", marginBottom: "10px" }}
        />
        <h2>{coupon.title}</h2>
        <p>
          <strong>Discount:</strong> {coupon.discount}% off
        </p>
        <p>
          <strong>Expires:</strong> {coupon.expiry}
        </p>
        <p style={{ color: coupon.businesses.brand_color }}>
          {coupon.businesses.tagline}
        </p>

        {expired ? (
          <p style={{ color: "red" }}>⚠️ This coupon has expired</p>
        ) : claimed ? (
          <p style={{ color: "green" }}>✅ Coupon claimed!</p>
        ) : (
          <div style={{ marginTop: "20px" }}>
            <p>Enter your postcode to claim:</p>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. PR1"
              style={{ padding: "6px", marginBottom: "10px" }}
            />
            <br />
            <button
              onClick={handleClaim}
              disabled={!postcode}
              style={{ padding: "10px 20px" }}
            >
              Claim This Offer
            </button>
          </div>
        )}
      </div>
    </>
  );
}
